import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Building2, MapPin, Globe, Users, Star, Briefcase,
  ChevronRight, MessageSquare, Loader2, ThumbsUp,
  Edit3, ExternalLink, Heart, Flag, Shield, CheckCircle,
  Linkedin, Mail, BarChart3, ArrowUpDown, X, Send,
  AlertTriangle, Clock, Briefcase as BriefcaseIcon
} from "lucide-react";

const StarRating = ({ rating, size = "w-4 h-4" }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`${size} ${s <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-gray-600"}`} />
    ))}
  </div>
);

const RatingBar = ({ label, count, total }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-14 text-gray-400">{label}</span>
      <div className="flex-1 h-2 bg-[#222] rounded-full overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-6 text-right text-gray-500">{count}</span>
    </div>
  );
};

const CategoryRating = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-400">{label}</span>
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-[#222] rounded-full overflow-hidden">
        <div className="h-full bg-crimson rounded-full" style={{ width: `${(value / 5) * 100}%` }} />
      </div>
      <span className="text-white font-medium w-6 text-right">{value || "—"}</span>
    </div>
  </div>
);

const REVIEWER_TYPE_LABELS = {
  current_employee: "Current Employee",
  former_employee: "Former Employee",
  interview_candidate: "Interview Candidate",
  intern_contractor: "Intern / Contractor"
};

const DEPARTMENTS = [
  "Engineering", "Product", "Design", "Marketing", "Sales", "HR",
  "Finance", "Legal", "Operations", "Customer Support", "Data Science",
  "DevOps", "QA", "Executive", "Admin", "Other"
];

const FLAG_REASONS = [
  { value: "fake_review", label: "Fake review" },
  { value: "offensive_content", label: "Offensive content" },
  { value: "wrong_company", label: "Wrong company" },
  { value: "spam", label: "Spam" },
  { value: "other", label: "Other" }
];

const CompanyProfile = () => {
  const { companyName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating_overall: 5, rating_work_life: 0, rating_compensation: 0, rating_management: 0,
    title: "", pros: "", cons: "", reviewer_type: "former_employee",
    job_title: "", department: "", employment_period: "", recommend: null,
    interview_difficulty: "", interview_experience: "", time_to_feedback: "",
    verification_email: "", verification_linkedin: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [allReviews, setAllReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [departments, setDepartments] = useState([]);
  const [interviewStats, setInterviewStats] = useState({});
  const [interviewReviews, setInterviewReviews] = useState([]);
  const [sortBy, setSortBy] = useState("recent");
  const [deptFilter, setDeptFilter] = useState("");
  const [activeTab, setActiveTab] = useState("reviews"); // reviews, interviews, departments
  const [flagModal, setFlagModal] = useState(null);
  const [responseModal, setResponseModal] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    if (companyName) {
      fetchCompany();
      fetchReviews();
      fetchAiSummary();
    }
  }, [companyName]);

  useEffect(() => {
    fetchReviews();
  }, [sortBy, deptFilter]);

  const fetchCompany = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/companies/${encodeURIComponent(companyName)}`, { credentials: "include", headers });
      if (res.ok) setCompany(await res.json());
      else toast.error("Company not found");
    } catch { toast.error("Failed to load company"); }
    finally { setLoading(false); }
  };

  const fetchReviews = async (page = 1) => {
    try {
      let url = `${API}/reviews/company/${encodeURIComponent(companyName)}?page=${page}&limit=20&sort=${sortBy}`;
      if (deptFilter) url += `&department=${encodeURIComponent(deptFilter)}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setAllReviews(data.reviews || []);
        setReviewStats(data.stats || {});
        setDepartments(data.departments || []);
        setInterviewStats(data.interview_stats || {});
        setInterviewReviews(data.interview_reviews || []);
      }
    } catch {}
  };

  const fetchAiSummary = async () => {
    setLoadingSummary(true);
    try {
      const res = await fetch(`${API}/reviews/company/${encodeURIComponent(companyName)}/ai-summary`);
      if (res.ok) {
        const data = await res.json();
        if (data.summary !== null) setAiSummary(data);
      }
    } catch {}
    finally { setLoadingSummary(false); }
  };

  const submitReview = async () => {
    if (!reviewForm.title.trim()) return toast.error("Title is required");
    if (!reviewForm.pros.trim()) return toast.error("Pros are required");
    if (!reviewForm.cons.trim()) return toast.error("Cons are required");
    if (reviewForm.rating_overall < 1) return toast.error("Overall rating is required");
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const body = { ...reviewForm, company_name: company?.company_name || companyName };
      // Clean zero ratings to null
      ["rating_work_life", "rating_compensation", "rating_management"].forEach(k => { if (body[k] === 0) body[k] = null; });
      if (!body.interview_difficulty) { body.interview_difficulty = null; body.interview_experience = null; body.time_to_feedback = null; }
      if (!body.verification_email) body.verification_email = null;
      if (!body.verification_linkedin) body.verification_linkedin = null;
      if (body.recommend === null) delete body.recommend;

      const res = await fetch(`${API}/reviews`, { method: "POST", credentials: "include", headers, body: JSON.stringify(body) });
      if (res.ok) {
        toast.success("Review submitted!");
        setShowReviewForm(false);
        setReviewForm({ rating_overall: 5, rating_work_life: 0, rating_compensation: 0, rating_management: 0, title: "", pros: "", cons: "", reviewer_type: "former_employee", job_title: "", department: "", employment_period: "", recommend: null, interview_difficulty: "", interview_experience: "", time_to_feedback: "", verification_email: "", verification_linkedin: "" });
        fetchCompany(); fetchReviews();
      } else { const err = await res.json(); toast.error(err.detail || "Failed to submit"); }
    } catch { toast.error("Failed to submit review"); }
    finally { setSubmitting(false); }
  };

  const markHelpful = async (reviewId) => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API}/reviews/${reviewId}/helpful`, { method: "POST", credentials: "include", headers });
      if (res.ok) { toast.success("Marked as helpful"); fetchReviews(); }
      else { const err = await res.json(); toast.error(err.detail || "Already voted"); }
    } catch {}
  };

  const submitFlag = async (reviewId, reason) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/reviews/${reviewId}/flag`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ reason })
      });
      if (res.ok) { toast.success("Review reported"); setFlagModal(null); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to report"); }
    } catch { toast.error("Failed to report"); }
  };

  const submitResponse = async (reviewId) => {
    if (!responseText.trim()) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/reviews/${reviewId}/respond`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ response_text: responseText })
      });
      if (res.ok) { toast.success("Response posted"); setResponseModal(null); setResponseText(""); fetchReviews(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed"); }
    } catch { toast.error("Failed to post response"); }
  };

  const inputClass = "w-full bg-obsidian border border-[#333] rounded px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none";

  if (loading) return (
    <div className="min-h-screen bg-obsidian text-white"><Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-crimson" /></main>
    </div>
  );

  const stats = reviewStats;
  const isRecruiter = user?.role === "recruiter" || user?.role === "admin";

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Company Header */}
          <div className="bg-obsidian-light border border-[#222] rounded-lg p-6">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="w-16 h-16 rounded-lg flex-shrink-0 overflow-hidden">
                {company?.company_logo ? (
                  <img src={company.company_logo} alt={company.company_name} className="w-16 h-16 rounded-lg object-cover border border-[#333]" />
                ) : (
                  <div className="w-16 h-16 bg-crimson/10 border border-crimson/20 rounded-lg flex items-center justify-center"><Building2 className="w-8 h-8 text-crimson" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold" data-testid="company-name">{company?.company_name || companyName}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-400">
                  {company?.industry && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{company.industry}</span>}
                  {company?.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{company.location}</span>}
                  {company?.company_size && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{company.company_size}</span>}
                  {company?.company_website && (
                    <a href={company.company_website.startsWith("http") ? company.company_website : `https://${company.company_website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-crimson hover:underline"><Globe className="w-3.5 h-3.5" />Website</a>
                  )}
                </div>
                {company?.company_description && <p className="text-gray-400 mt-3 text-sm">{company.company_description}</p>}
              </div>
              <Button onClick={() => setShowReviewForm(!showReviewForm)} variant="outline" className="border-[#333] text-gray-300 hover:bg-white/5" data-testid="write-review-btn">
                <Edit3 className="w-4 h-4 mr-2" /> Write Review
              </Button>
            </div>
          </div>

          {/* AI Review Summary */}
          {aiSummary?.summary && (
            <motion.div
              className="bg-obsidian-light border border-[#222] rounded-lg p-5"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="ai-summary"
            >
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-crimson" />
                <h3 className="font-heading text-sm font-bold uppercase">AI Workplace Summary</h3>
                <span className={`px-2 py-0.5 text-[10px] font-bold border ${
                  aiSummary.overall_sentiment === "positive" ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10"
                  : aiSummary.overall_sentiment === "negative" ? "text-rose-400 border-rose-400/30 bg-rose-400/10"
                  : "text-amber-400 border-amber-400/30 bg-amber-400/10"
                }`}>{aiSummary.overall_sentiment?.toUpperCase()}</span>
                <span className="text-[10px] text-gray-600 ml-auto">Based on {aiSummary.review_count} reviews</span>
              </div>
              <p className="text-sm text-gray-300 mb-3">{aiSummary.summary}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {aiSummary.top_pros?.length > 0 && (
                  <div>
                    <p className="text-xs text-emerald-400 font-medium mb-1.5">Top Pros</p>
                    <ul className="space-y-1">{aiSummary.top_pros.map((p, i) => (
                      <li key={i} className="text-xs text-gray-400 flex items-start gap-1.5"><CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />{p}</li>
                    ))}</ul>
                  </div>
                )}
                {aiSummary.top_cons?.length > 0 && (
                  <div>
                    <p className="text-xs text-rose-400 font-medium mb-1.5">Top Cons</p>
                    <ul className="space-y-1">{aiSummary.top_cons.map((c, i) => (
                      <li key={i} className="text-xs text-gray-400 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 text-rose-400 mt-0.5 flex-shrink-0" />{c}</li>
                    ))}</ul>
                  </div>
                )}
              </div>
              {aiSummary.culture_insight && (
                <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-[#222] italic">{aiSummary.culture_insight}</p>
              )}
            </motion.div>
          )}

          {/* Review Form */}
          <AnimatePresence>
            {showReviewForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="bg-obsidian-light border border-[#222] rounded-lg p-6 space-y-4" data-testid="review-form">
                  <h3 className="font-heading text-lg font-bold">Write a Structured Review</h3>

                  {/* Row 1: Reviewer Type + Job Title + Department */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Reviewer Type *</label>
                      <select value={reviewForm.reviewer_type} onChange={(e) => setReviewForm(p => ({ ...p, reviewer_type: e.target.value }))} className={inputClass} data-testid="reviewer-type">
                        <option value="current_employee">Current Employee</option>
                        <option value="former_employee">Former Employee</option>
                        <option value="interview_candidate">Interview Candidate</option>
                        <option value="intern_contractor">Intern / Contractor</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Job Title</label>
                      <input value={reviewForm.job_title} onChange={(e) => setReviewForm(p => ({ ...p, job_title: e.target.value }))} placeholder="e.g. Software Engineer" className={inputClass} data-testid="job-title" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Department</label>
                      <select value={reviewForm.department} onChange={(e) => setReviewForm(p => ({ ...p, department: e.target.value }))} className={inputClass} data-testid="department">
                        <option value="">Select...</option>
                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Ratings */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-2">Ratings</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[
                        { key: "rating_overall", label: "Overall *" },
                        { key: "rating_work_life", label: "Work-Life Balance" },
                        { key: "rating_compensation", label: "Compensation" },
                        { key: "rating_management", label: "Management" },
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <p className="text-xs text-gray-500 mb-1">{label}</p>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <button key={s} type="button" onClick={() => setReviewForm(p => ({ ...p, [key]: s }))} data-testid={`${key}-star-${s}`}>
                                <Star className={`w-5 h-5 ${s <= reviewForm[key] ? "text-amber-400 fill-amber-400" : "text-gray-600"}`} />
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Title + Pros + Cons */}
                  <input value={reviewForm.title} onChange={(e) => setReviewForm(p => ({ ...p, title: e.target.value }))} placeholder="Review title *" className={inputClass} data-testid="review-title" />
                  <textarea value={reviewForm.pros} onChange={(e) => setReviewForm(p => ({ ...p, pros: e.target.value }))} placeholder="What are the pros? *" rows={3} className={inputClass} data-testid="review-pros" />
                  <textarea value={reviewForm.cons} onChange={(e) => setReviewForm(p => ({ ...p, cons: e.target.value }))} placeholder="What are the cons? *" rows={3} className={inputClass} data-testid="review-cons" />

                  {/* Recommend */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Would you recommend this company?</label>
                    <div className="flex gap-3">
                      {[{ val: true, label: "Yes" }, { val: false, label: "No" }].map(opt => (
                        <button key={String(opt.val)} type="button" onClick={() => setReviewForm(p => ({ ...p, recommend: opt.val }))}
                          className={`px-4 py-1.5 text-sm border transition-colors ${reviewForm.recommend === opt.val ? "border-crimson text-crimson bg-crimson/10" : "border-[#333] text-gray-500 hover:border-[#555]"}`}
                          data-testid={`recommend-${opt.val}`}
                        >{opt.label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Interview Experience (conditional) */}
                  {(reviewForm.reviewer_type === "interview_candidate" || reviewForm.reviewer_type === "intern_contractor") && (
                    <div className="border-t border-[#222] pt-4 mt-2">
                      <h4 className="text-sm font-bold text-white mb-3">Interview Experience</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Difficulty</label>
                          <select value={reviewForm.interview_difficulty} onChange={(e) => setReviewForm(p => ({ ...p, interview_difficulty: e.target.value }))} className={inputClass} data-testid="interview-difficulty">
                            <option value="">Select...</option>
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Time to Feedback</label>
                          <input value={reviewForm.time_to_feedback} onChange={(e) => setReviewForm(p => ({ ...p, time_to_feedback: e.target.value }))} placeholder="e.g. 2 weeks" className={inputClass} data-testid="time-to-feedback" />
                        </div>
                      </div>
                      <textarea value={reviewForm.interview_experience} onChange={(e) => setReviewForm(p => ({ ...p, interview_experience: e.target.value }))} placeholder="Describe the interview process..." rows={3} className={`${inputClass} mt-3`} data-testid="interview-experience" />
                    </div>
                  )}

                  {/* Verification (optional) */}
                  <div className="border-t border-[#222] pt-4 mt-2">
                    <h4 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-400" /> Optional Verification</h4>
                    <p className="text-xs text-gray-500 mb-3">Verified reviews get a badge and are more trusted</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Work Email</label>
                        <input value={reviewForm.verification_email} onChange={(e) => setReviewForm(p => ({ ...p, verification_email: e.target.value }))} placeholder="you@company.com" className={inputClass} data-testid="verify-email" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><Linkedin className="w-3 h-3" /> LinkedIn Profile</label>
                        <input value={reviewForm.verification_linkedin} onChange={(e) => setReviewForm(p => ({ ...p, verification_linkedin: e.target.value }))} placeholder="https://linkedin.com/in/..." className={inputClass} data-testid="verify-linkedin" />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button onClick={submitReview} disabled={submitting} className="bg-crimson hover:bg-crimson/80" data-testid="submit-review">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Submit Review
                    </Button>
                    <Button onClick={() => setShowReviewForm(false)} variant="outline" className="border-[#333]">Cancel</Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Tabs + Reviews */}
            <div className="lg:col-span-2 space-y-4">
              {/* Tabs */}
              <div className="flex gap-2 border-b border-[#222] pb-2">
                {[
                  { id: "reviews", label: "Reviews", count: stats.total || 0 },
                  { id: "interviews", label: "Interview Insights", count: interviewStats.total || 0 },
                  { id: "departments", label: "Departments", count: departments.length },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-1.5 text-sm transition-colors ${activeTab === tab.id ? "text-crimson border-b-2 border-crimson font-medium" : "text-gray-500 hover:text-white"}`}
                    data-testid={`tab-${tab.id}`}
                  >{tab.label} ({tab.count})</button>
                ))}
              </div>

              {/* Reviews Tab */}
              {activeTab === "reviews" && (
                <div className="space-y-4">
                  {/* Sort */}
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-gray-500" />
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-obsidian border border-[#333] text-sm text-white px-2 py-1 focus:outline-none" data-testid="sort-reviews">
                      <option value="recent">Most Recent</option>
                      <option value="highest">Highest Rated</option>
                      <option value="lowest">Lowest Rated</option>
                    </select>
                    {deptFilter && (
                      <span className="text-xs text-crimson flex items-center gap-1">
                        Dept: {deptFilter} <button onClick={() => setDeptFilter("")}><X className="w-3 h-3" /></button>
                      </span>
                    )}
                  </div>

                  {allReviews.length === 0 ? (
                    <p className="text-gray-500 text-center py-10">No reviews yet. Be the first to review!</p>
                  ) : allReviews.map(r => (
                    <div key={r.review_id} className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-3" data-testid={`review-${r.review_id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <StarRating rating={r.rating_overall || r.rating} size="w-3.5 h-3.5" />
                            {r.is_verified && (
                              <span className="flex items-center gap-1 text-[10px] text-emerald-400 border border-emerald-400/30 px-1.5 py-0.5 bg-emerald-400/10">
                                <CheckCircle className="w-3 h-3" /> Verified
                              </span>
                            )}
                          </div>
                          <p className="font-medium text-sm mt-1">{r.title}</p>
                        </div>
                        <div className="text-xs text-gray-500 text-right flex-shrink-0 ml-3">
                          {r.job_title && <p className="font-medium text-gray-400">{r.job_title}</p>}
                          <p>{REVIEWER_TYPE_LABELS[r.reviewer_type] || r.employment_status}</p>
                          {r.department && <p className="text-gray-600">{r.department}</p>}
                        </div>
                      </div>

                      {/* Category ratings if present */}
                      {(r.rating_work_life || r.rating_compensation || r.rating_management) && (
                        <div className="flex gap-4 text-xs text-gray-500">
                          {r.rating_work_life && <span>Work-Life: <span className="text-white">{r.rating_work_life}/5</span></span>}
                          {r.rating_compensation && <span>Compensation: <span className="text-white">{r.rating_compensation}/5</span></span>}
                          {r.rating_management && <span>Management: <span className="text-white">{r.rating_management}/5</span></span>}
                        </div>
                      )}

                      {r.pros && <div><span className="text-xs text-emerald-400 font-medium">Pros: </span><span className="text-sm text-gray-300">{r.pros}</span></div>}
                      {r.cons && <div><span className="text-xs text-rose-400 font-medium">Cons: </span><span className="text-sm text-gray-300">{r.cons}</span></div>}

                      {r.recommend !== null && r.recommend !== undefined && (
                        <p className="text-xs"><span className="text-gray-500">Recommends: </span><span className={r.recommend ? "text-emerald-400" : "text-rose-400"}>{r.recommend ? "Yes" : "No"}</span></p>
                      )}

                      {/* Company Response */}
                      {r.company_response && (
                        <div className="bg-[#0a0a0a] border-l-2 border-crimson p-3 mt-2">
                          <p className="text-xs text-crimson font-medium mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company Response</p>
                          <p className="text-sm text-gray-300">{r.company_response.response_text}</p>
                          <p className="text-[10px] text-gray-600 mt-1">— {r.company_response.responder_name}</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex items-center gap-4">
                          <button onClick={() => markHelpful(r.review_id)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-white" data-testid={`helpful-${r.review_id}`}>
                            <ThumbsUp className="w-3.5 h-3.5" /> Helpful ({r.helpful_count || 0})
                          </button>
                          <button onClick={() => setFlagModal(r.review_id)} className="flex items-center gap-1 text-xs text-gray-600 hover:text-rose-400" data-testid={`flag-${r.review_id}`}>
                            <Flag className="w-3 h-3" /> Report
                          </button>
                          {isRecruiter && !r.company_response && (
                            <button onClick={() => { setResponseModal(r.review_id); setResponseText(""); }} className="flex items-center gap-1 text-xs text-gray-500 hover:text-crimson">
                              <MessageSquare className="w-3 h-3" /> Respond
                            </button>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-600">{r.user_name} · {new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Interview Insights Tab */}
              {activeTab === "interviews" && (
                <div className="space-y-4">
                  {interviewStats.total > 0 && (
                    <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                      <h3 className="font-semibold text-sm mb-3">Interview Difficulty</h3>
                      <div className="flex gap-4">
                        {[{ key: "easy", label: "Easy", color: "emerald" }, { key: "medium", label: "Medium", color: "amber" }, { key: "hard", label: "Hard", color: "rose" }].map(d => (
                          <div key={d.key} className="text-center flex-1">
                            <p className={`text-2xl font-bold text-${d.color}-400`}>{interviewStats[d.key] || 0}</p>
                            <p className="text-xs text-gray-500">{d.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {interviewReviews.length === 0 ? (
                    <p className="text-gray-500 text-center py-10">No interview reviews yet</p>
                  ) : interviewReviews.map(r => (
                    <div key={r.review_id} className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-medium border ${r.interview_difficulty === "easy" ? "text-emerald-400 border-emerald-400/30" : r.interview_difficulty === "hard" ? "text-rose-400 border-rose-400/30" : "text-amber-400 border-amber-400/30"}`}>
                          {r.interview_difficulty?.toUpperCase()}
                        </span>
                        {r.job_title && <span className="text-sm text-gray-400">{r.job_title}</span>}
                        {r.time_to_feedback && <span className="text-xs text-gray-500">Feedback: {r.time_to_feedback}</span>}
                      </div>
                      {r.interview_experience && <p className="text-sm text-gray-300">{r.interview_experience}</p>}
                      <p className="text-[10px] text-gray-600">{r.user_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Departments Tab */}
              {activeTab === "departments" && (
                <div className="space-y-3">
                  {departments.length === 0 ? (
                    <p className="text-gray-500 text-center py-10">No department data yet</p>
                  ) : departments.map(d => (
                    <button key={d.department} onClick={() => { setDeptFilter(d.department); setActiveTab("reviews"); }}
                      className="w-full flex items-center justify-between bg-obsidian-light border border-[#222] rounded-lg p-4 hover:border-crimson/30 transition-colors text-left"
                      data-testid={`dept-${d.department}`}
                    >
                      <div>
                        <h4 className="font-medium text-white">{d.department}</h4>
                        <p className="text-xs text-gray-500">{d.count} review{d.count !== 1 ? "s" : ""}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StarRating rating={d.avg_rating} size="w-3.5 h-3.5" />
                        <span className="text-sm font-bold text-amber-400">{d.avg_rating}</span>
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="space-y-4">
              {/* Overall Rating */}
              <div className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-4">
                <h3 className="font-semibold text-sm">Overall Rating</h3>
                <div className="text-center py-2">
                  <p className="text-4xl font-bold">{stats.avg_rating || "N/A"}</p>
                  <StarRating rating={stats.avg_rating || 0} size="w-5 h-5" />
                  <p className="text-xs text-gray-500 mt-1">{stats.total || 0} reviews</p>
                  {stats.recommend_total > 0 && (
                    <p className="text-xs text-emerald-400 mt-1">{Math.round((stats.recommend_yes / stats.recommend_total) * 100)}% recommend</p>
                  )}
                </div>
                <div className="space-y-2">
                  <RatingBar label="5 star" count={stats.five_star || 0} total={stats.total || 0} />
                  <RatingBar label="4 star" count={stats.four_star || 0} total={stats.total || 0} />
                  <RatingBar label="3 star" count={stats.three_star || 0} total={stats.total || 0} />
                  <RatingBar label="2 star" count={stats.two_star || 0} total={stats.total || 0} />
                  <RatingBar label="1 star" count={stats.one_star || 0} total={stats.total || 0} />
                </div>
              </div>

              {/* Category Breakdown */}
              {(stats.avg_work_life > 0 || stats.avg_compensation > 0 || stats.avg_management > 0) && (
                <div className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-3">
                  <h3 className="font-semibold text-sm">Category Ratings</h3>
                  <CategoryRating label="Work-Life Balance" value={stats.avg_work_life} />
                  <CategoryRating label="Compensation" value={stats.avg_compensation} />
                  <CategoryRating label="Management" value={stats.avg_management} />
                </div>
              )}

              {/* Company Info */}
              <div className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-3">
                <h3 className="font-semibold text-sm">Company Info</h3>
                {[
                  { label: "Industry", value: company?.industry },
                  { label: "Location", value: company?.location },
                  { label: "Size", value: company?.company_size },
                  { label: "Active Jobs", value: company?.active_jobs_count },
                ].filter(i => i.value).map(i => (
                  <div key={i.label} className="flex justify-between text-sm">
                    <span className="text-gray-500">{i.label}</span>
                    <span className="text-gray-300">{i.value}</span>
                  </div>
                ))}
              </div>

              {/* Benefits */}
              {company?.company_benefits?.length > 0 && (
                <div className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-3" data-testid="company-benefits">
                  <h3 className="font-semibold text-sm flex items-center gap-2"><Heart className="w-4 h-4 text-crimson" /> Employee Benefits</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {company.company_benefits.map((b, i) => (
                      <span key={i} className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs">{b}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Flag Modal */}
      <AnimatePresence>
        {flagModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setFlagModal(null)} />
            <motion.div className="relative bg-obsidian-light border border-[#333] w-full max-w-sm p-6" initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} data-testid="flag-modal">
              <h3 className="font-bold flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-rose-400" /> Report Review</h3>
              <div className="space-y-2">
                {FLAG_REASONS.map(r => (
                  <button key={r.value} onClick={() => submitFlag(flagModal, r.value)}
                    className="w-full text-left px-4 py-2.5 bg-obsidian border border-[#222] text-sm text-gray-300 hover:border-rose-400/50 hover:text-rose-400 transition-colors"
                    data-testid={`flag-reason-${r.value}`}
                  >{r.label}</button>
                ))}
              </div>
              <Button onClick={() => setFlagModal(null)} variant="outline" className="w-full mt-3 border-[#333]">Cancel</Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Response Modal */}
      <AnimatePresence>
        {responseModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setResponseModal(null)} />
            <motion.div className="relative bg-obsidian-light border border-[#333] w-full max-w-md p-6" initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} data-testid="response-modal">
              <h3 className="font-bold flex items-center gap-2 mb-4"><Building2 className="w-5 h-5 text-crimson" /> Company Response</h3>
              <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} placeholder="Write your official response..." rows={4} className={inputClass} maxLength={2000} data-testid="response-text" />
              <p className="text-xs text-gray-600 mt-1 text-right">{responseText.length}/2000</p>
              <div className="flex gap-3 mt-3">
                <Button onClick={() => submitResponse(responseModal)} disabled={!responseText.trim()} className="bg-crimson hover:bg-crimson/80">Post Response</Button>
                <Button onClick={() => setResponseModal(null)} variant="outline" className="border-[#333]">Cancel</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanyProfile;
